import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class HardwareFingerprintService {
    private currentGenerationPromise: Promise<string> | null = null;

    public generateStableId(): Promise<string> {
        if (this.currentGenerationPromise) {
            return this.currentGenerationPromise;
        }

        this.currentGenerationPromise = this.executeFingerprintGeneration();

        return this.currentGenerationPromise;
    }

  public async executeFingerprintGeneration(): Promise<string> {
    try {
        const audioHash = await this.getAudioFingerprint();
        const webglData = this.getAdvancedWebGLFingerprint();

        const components = {
          os: this.getStablePlatform(),
          hardware: {
            cores: navigator.hardwareConcurrency || 'unknown',
            memory: (navigator as any).deviceMemory || 'unknown',
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          audio: audioHash,
          gpu: webglData.renderer,
          gpuExtensions: webglData.extensions
        };

        return this.utf8Btoa(JSON.stringify(components));
    } finally {
        this.currentGenerationPromise = null;
    }
  }

  private getStablePlatform(): string {
    const uaData = (navigator as any).userAgentData;
    if (uaData && uaData.platform) return uaData.platform;

    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('windows')) return 'Windows';
    if (ua.includes('mac')) return 'macOS';
    if (ua.includes('linux')) return 'Linux';
    if (ua.includes('android')) return 'Android';
    if (ua.includes('iphone') || ua.includes('ipad')) return 'iOS';

    return 'Unknown';
  }

  private getAdvancedWebGLFingerprint(): { renderer: string, extensions: string[] } {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

      if (!gl) return { renderer: 'no-webgl', extensions: [] };

      const context = gl as WebGLRenderingContext;
      let renderer = 'unknown';

      const debugInfo = context.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const vendor = context.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        const gpu = context.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        renderer = `${vendor}~${gpu}`;
      }

      const extensions = context.getSupportedExtensions() || [];

      return { renderer, extensions };
    } catch (e) {
      return { renderer: 'webgl-error', extensions: [] };
    }
  }

  private async getAudioFingerprint(): Promise<string> {
    try {
      const audioCtx = new (window.OfflineAudioContext || (window as any).webkitOfflineAudioContext)(1, 44100, 44100);
      const oscillator = audioCtx.createOscillator();
      const compressor = audioCtx.createDynamicsCompressor();

      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(10000, audioCtx.currentTime);

      compressor.threshold.setValueAtTime(-50, audioCtx.currentTime);
      compressor.knee.setValueAtTime(40, audioCtx.currentTime);
      compressor.ratio.setValueAtTime(12, audioCtx.currentTime);
      compressor.attack.setValueAtTime(0, audioCtx.currentTime);
      compressor.release.setValueAtTime(0.25, audioCtx.currentTime);

      oscillator.connect(compressor);
      compressor.connect(audioCtx.destination);
      oscillator.start(0);

      const buffer = await audioCtx.startRendering();
      const channelData = buffer.getChannelData(0);

      let sum = 0;
      for (let i = 4500; i < 5000; i++) {
        sum += Math.abs(channelData[i]);
      }

      return sum.toString();
    } catch (e) {
      return 'audio-unsupported';
    }
  }
  private utf8Btoa(value: string) {
    const bytes = new TextEncoder().encode(value);
    const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');

    return btoa(binString);
  }
}
